import '../../../../styles/knowledge-concept/quiz/QuizPage.css';

import { MathJax, MathJaxContext } from 'better-react-mathjax';
import React, { useEffect, useRef, useState } from 'react';

import Label from '../../../atoms/Label';
import ic_hint from '../../../../assets/ic_hint.svg';
import { logger } from '../../../../utils/logger';
import { saveUserQuizResponse } from '../../../../services/DashboardApi';
import { set } from 'lodash';
import { logUserActivity, updateXPMetrics } from '../../../../services/UsersApi';

const QuizPage = ({ currentQuestion, topicId, subtopicId }) => {
	const [selectedAnswers, setSelectedAnswers] = useState({});
	const [questionStatus, setQuestionStatus] = useState(Array(1).fill('not-visited'));
	const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
	const [showPartialHint, setShowPartialHint] = useState(false);
	const [showCompleteHint, setShowCompleteHint] = useState(false);
	const [showStepHint, setShowStepHint] = useState(false);
	const [showExplanation, setShowExplanation] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [apiResponse, setApiResponse] = useState(null);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [stepHints, setStepHints] = useState([]);
	const hintRef = useRef(null);
	const [startTime, setStartTime] = useState(null);

	useEffect(() => {
		if (currentQuestion && !currentQuestion.ATTEMPTED) {
			setStartTime(new Date().toISOString());
		}
	}, [currentQuestion]);

	const handleAnswerSelection = (questionId, answerIndex) => {
		setSelectedAnswers({ ...selectedAnswers, [questionId]: answerIndex });
	};

	const handleSubmit = async (questionIndex) => {
		// Update question status
		const updatedStatus = [...questionStatus];
		const hasAnswer = selectedAnswers[1] !== undefined;
		updatedStatus[questionIndex] = hasAnswer ? 'answered' : 'unanswered';
		setQuestionStatus(updatedStatus);

		// If no answer is selected, don't proceed with API call
		if (!hasAnswer) return;

		try {
			setIsSubmitting(true);

			// Check if the selected answer is correct
			const selectedAnswerId = selectedAnswers[1];
			const isCorrect = currentQuestion.correctAnswer === selectedAnswerId;

			logger.log('Selected answer ID:', selectedAnswerId);
			logger.log('Is correct:', isCorrect);
			// Prepare data for API call
			const quizData = {
				CONCEPT_QUIZ_ID: currentQuestion.id,
				USER_ANSWER: selectedAnswerId,
				IS_CORRECT: isCorrect,
			};

			// Call the API
			const response = await saveUserQuizResponse(quizData);
			setApiResponse(response);

			// You can add additional logic here based on the response
			logger.log('Quiz response saved:', response);
			const endTime = new Date().toISOString();
			const durationSec = Math.round((new Date(endTime) - new Date(startTime)) / 1000);

			await logUserActivity({
				userId: currentQuestion.userId,
				eventType: 'KC_CONCEPT_QUIZ',
				startTime: startTime,
				endTime: endTime,
				durationSec,
				topicId: topicId,
				subtopicId: subtopicId,
				// notebookId: currentQuestion.notebookId,
				// questionId: currentQuestion.questionId,
			});
			setStartTime(null); // Reset start time after submission

			await updateXPMetrics(currentQuestion.userId, 'QUIZ', 1);
		} catch (error) {
			logger.log('Failed to save quiz response:', error);
			// Handle error (you might want to show an error message to the user)
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleHintRequest = (type) => {
		// First close any existing hints
		setShowPartialHint(false);
		setShowCompleteHint(false);
		setShowStepHint(false);

		// Close the modal
		setIsModalOpen(false);

		// Then set the requested hint after a small delay
		setTimeout(() => {
			if (type === 'partial') {
				setShowPartialHint(true);
			} else if (type === 'complete') {
				setShowCompleteHint(true);
			} else if (type === 'step-hint') {
				// Parse step hints from step_hint
				if (currentQuestion.step_hint) {
					let steps = [];

					// Check if step_hint is an array (new format)
					if (Array.isArray(currentQuestion.step_hint)) {
						logger.log('Using new array format for step hints');
						steps = currentQuestion.step_hint.map((step) => {
							// Extract text from the HTML paragraph tag
							const text = step.text || '';
							return text.replace(/^<p>|<\/p>$/g, '').trim();
						});

						// If we have steps from the array format, set them directly
						if (steps.length > 0) {
							logger.log('Parsed steps from array format:', steps);
							setStepHints(steps);
							setCurrentStepIndex(0);
							setShowStepHint(true);
							return; // Exit early since we've found steps
						}
					} else {
						// Legacy format parsing
						logger.log('Using legacy format for step hints');
						// Sample legacy step hint format: "<p>1. Identify the formula... <br/> 2. Substitute... <br/> 3. Calculate...</p>"

						// Get step hint content
						const stepContent = currentQuestion.step_hint;

						// Split by <br/> tags first to separate steps
						const stepsByBreaks = stepContent
							.replace(/^<p>|<\/p>$/g, '')
							.split(/<br\/>\s*/);

						// Extract step content from each part
						stepsByBreaks.forEach((part) => {
							// Look for step number pattern (digit followed by period)
							const stepMatch = part.match(/^\s*(\d+)\.\s*(.*?)$/);
							if (stepMatch) {
								// Extract the step content
								steps.push(stepMatch[2].trim());
							}
						});

						// Fallback approach if no steps were found
						if (steps.length === 0) {
							// Try direct parsing of the original format
							logger.log('Using fallback parsing for steps');

							try {
								// Extract numbered steps using a more direct method
								const cleanContent = stepContent
									.replace(/<p>|<\/p>/g, '') // Remove <p> tags
									.replace(/\s+/g, ' '); // Normalize whitespace

								// Create a temporary element to help parse HTML content
								const tempDiv = document.createElement('div');
								tempDiv.innerHTML = cleanContent;
								const plainText = tempDiv.textContent || tempDiv.innerText || '';

								// Split by step numbers (1., 2., etc.)
								const fallbackSteps = [];
								const stepRegex = /(\d+)\.\s+([^0-9.]+)(?=\s*\d+\.|$)/g;
								let match;

								while ((match = stepRegex.exec(plainText)) !== null) {
									fallbackSteps.push(match[2].trim());
								}

								if (fallbackSteps.length > 0) {
									logger.log('Fallback parsed steps:', fallbackSteps);
									setStepHints(fallbackSteps);
									setCurrentStepIndex(0);
									setShowStepHint(true);
								} else {
									// Last resort: Just try to split by numbers
									const simpleSteps = plainText
										.split(/\d+\.\s+/)
										.filter(Boolean)
										.map((step) => step.trim());
									if (simpleSteps.length > 0) {
										logger.log('Simple parsed steps:', simpleSteps);
										setStepHints(simpleSteps);
										setCurrentStepIndex(0);
										setShowStepHint(true);
									}
								}
							} catch (error) {
								logger.log('Error parsing step hints:', error);
							}
						}
					}
				}
			}

			// Scroll to the hint after it's rendered
			setTimeout(() => {
				if (hintRef.current) {
					// Get the parent question-section element
					const questionSection = hintRef.current.closest('.question-section');
					if (questionSection) {
						// Scroll the hint into view
						// First, get the hint box element (the actual content)
						const hintBox = hintRef.current.querySelector('.hint-box');
						if (hintBox) {
							// Calculate positions
							const hintBoxTop = hintBox.getBoundingClientRect().top;
							const questionSectionTop = questionSection.getBoundingClientRect().top;
							const relativeTop = hintBoxTop - questionSectionTop;

							// Scroll to position the hint at the top of the visible area with some padding
							questionSection.scrollTo({
								top: questionSection.scrollTop + relativeTop - 20,
								behavior: 'smooth',
							});
						} else {
							// If hint box not found yet, scroll to the hint container
							hintRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							});
							// Adjust scroll position to show full hint
							questionSection.scrollTop -= 20; // Add some padding at the top
						}
					} else {
						// Fallback to the original behavior if question-section not found
						hintRef.current.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						});
					}
				}
			}, 100); // Longer delay to ensure the hint is fully rendered
		}, 10);
	};

	const handleNextStep = () => {
		if (currentStepIndex < stepHints.length - 1) {
			setCurrentStepIndex(currentStepIndex + 1);
		}
	};

	const handlePrevStep = () => {
		if (currentStepIndex > 0) {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	};

	return (
		<MathJaxContext
			config={{
				tex: {
					inlineMath: [['\\(', '\\)']],
					displayMath: [['\\[', '\\]']],
				},
			}}
		>
			<div className="quiz-page">
				<div className="question-section">
					<div className="question-box">
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}
						>
							<h3 style={{ display: 'flex' }}>Question</h3>
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
								onClick={() => setIsModalOpen(true)}
							>
								{!(currentQuestion.ATTEMPTED || apiResponse?.data?.ATTEMPTED) && (
									<>
										<img
											src={ic_hint}
											alt="Question"
											style={{ marginLeft: '20px' }}
										/>
										<Label
											style={{ marginLeft: '5px' }}
											label="Hint"
											fontSize="14px"
											fontWeight="400"
											color="#000000"
											textAlign="left"
											padding="0px"
										/>
									</>
								)}
							</div>
						</div>
						{currentQuestion && (
							<>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
									}}
								>
									{/* <span style={{marginRight: "8px"}}>Q1)</span> */}
									<MathJax>
										<div
											className="quiz-question-text"
											style={{ textAlign: 'left' }}
											dangerouslySetInnerHTML={{
												__html: currentQuestion.question.text,
											}}
										/>
									</MathJax>
								</div>
								<div className="choices">
									<p style={{ display: 'flex', fontWeight: 'bold' }}>Choices</p>
									{currentQuestion.options.map((option) => {
										// Debug the entire currentQuestion object
										// logger.log("Current Question Object:", currentQuestion);

										const isAttempted =
											currentQuestion.ATTEMPTED ||
											apiResponse?.data?.ATTEMPTED;

										// Try all possible property names for correct answer
										var correctAnswerId =
											currentQuestion.answer ||
											currentQuestion.correctAnswer ||
											currentQuestion.CORRECT_ANSWER ||
											apiResponse?.data?.answer;

										var userAnswerId =
											currentQuestion.USER_ANSWER ||
											apiResponse?.data?.USER_ANSWER;

										// logger.log("Option:", option.id);
										// logger.log("Correct Answer (from multiple possible properties):", correctAnswerId);
										// logger.log("User Answer:", userAnswerId);
										// logger.log("Is Attempted:", isAttempted);

										// Determine the style class based on question state
										let styleClass = '';

										if (isAttempted) {
											// If question has been attempted
											// Convert to string for safe comparison
											const optionIdStr = String(option.id);
											const correctAnswerIdStr = String(correctAnswerId);
											const userAnswerIdStr = String(userAnswerId);

											if (optionIdStr === correctAnswerIdStr) {
												// This option is the correct answer - always green when attempted
												styleClass = 'correct-answer';
											} else if (
												optionIdStr === userAnswerIdStr &&
												userAnswerIdStr !== correctAnswerIdStr
											) {
												// This is the user's selected answer and it's wrong - red
												styleClass = 'incorrect-answer';
											}
										} else if (selectedAnswers[1] === option.id) {
											// Not yet attempted but selected in UI
											styleClass = 'selected';
										}

										return (
											<div
												key={option.id}
												className={`choice ${styleClass}`}
												onClick={() =>
													!isAttempted &&
													handleAnswerSelection(1, option.id)
												}
											>
												<span>{option.id}</span>
												<MathJax>
													<div
														style={{ textAlign: 'left' }}
														dangerouslySetInnerHTML={{
															__html: option.text,
														}}
													/>
												</MathJax>
											</div>
										);
									})}
								</div>

								{currentQuestion.ATTEMPTED || apiResponse?.data?.ATTEMPTED ? (
									<button
										className={`explanation-button`}
										onClick={() => setShowExplanation(true)}
									>
										{'Explanation'}
									</button>
								) : (
									<button
										className={`submit-button ${
											isSubmitting ? 'submitting' : ''
										}`}
										onClick={() => {
											setShowExplanation(false);
											setShowCompleteHint(false);
											setShowPartialHint(false);
											setShowStepHint(false);
											handleSubmit(0);
										}}
										disabled={isSubmitting}
									>
										{isSubmitting ? 'Submitting...' : 'Submit'}
									</button>
								)}

								{/* Hint containers */}
								{showPartialHint && (
									<div ref={hintRef} className="hint-container">
										<div className="hint-box">
											<button
												className="close-button"
												onClick={() => setShowPartialHint(false)}
											>
												×
											</button>
											<div className="hint-header">
												<h4>◆ Partial Hint</h4>
											</div>
											<MathJax>
												<div
													className="hint-text"
													dangerouslySetInnerHTML={{
														__html: currentQuestion.hint,
													}}
												/>
											</MathJax>
										</div>
									</div>
								)}
								{showCompleteHint && (
									<div ref={hintRef} className="hint-container">
										<div className="hint-box">
											<button
												className="close-button"
												onClick={() => setShowCompleteHint(false)}
											>
												×
											</button>
											<div className="hint-header">
												<h4>◆ Complete Hint</h4>
											</div>
											<MathJax>
												<div
													className="hint-text"
													dangerouslySetInnerHTML={{
														__html: currentQuestion.explanation,
													}}
												/>
											</MathJax>
										</div>
									</div>
								)}
								{showStepHint && (
									<div ref={hintRef} className="hint-container">
										<div className="hint-box">
											<button
												className="close-button"
												onClick={() => setShowStepHint(false)}
											>
												×
											</button>
											<div className="hint-header">
												<h4>◆ Step Hint</h4>
												<div className="step-counter">
													Step {currentStepIndex + 1} of{' '}
													{stepHints.length}
												</div>
											</div>
											<div className="hint-progress-bar-container">
												<div className="hint-progress-bar-background">
													<div
														className="hint-progress-bar-fill"
														style={{
															width: `${
																((currentStepIndex + 1) /
																	stepHints.length) *
																100
															}%`,
														}}
													/>
												</div>
												<div className="step-indicators">
													{stepHints.map((_, index) => (
														<div
															key={index}
															className={`step-dot ${
																index <= currentStepIndex
																	? 'completed'
																	: ''
															} ${
																index === currentStepIndex
																	? 'current'
																	: ''
															}`}
															onClick={() => {
																setCurrentStepIndex(index);
															}}
														>
															<span className="step-number">
																{index + 1}
															</span>
														</div>
													))}
												</div>
											</div>
											<MathJax>
												<div className="hint-text step-hint-text">
													<div
														className="step-content"
														style={{
															transform: `translateX(-${
																currentStepIndex * 100
															}%)`,
															transition:
																'transform 0.3s ease-in-out',
														}}
													>
														{stepHints.map((hint, index) => (
															<div key={index} className="step-slide">
																<div
																	dangerouslySetInnerHTML={{
																		__html: `${index + 1}. ${
																			hint || ''
																		}`,
																	}}
																/>
															</div>
														))}
													</div>
												</div>
											</MathJax>
											<div className="step-navigation">
												<button
													className="step-nav-button prev"
													onClick={handlePrevStep}
													disabled={currentStepIndex === 0}
												>
													← Previous
												</button>
												<button
													className="step-nav-button next"
													onClick={handleNextStep}
													disabled={
														currentStepIndex === stepHints.length - 1
													}
												>
													Next →
												</button>
											</div>
										</div>
									</div>
								)}
								{showExplanation && (
									<div ref={hintRef} className="hint-container">
										<div className="hint-box">
											<button
												className="close-button"
												onClick={() => setShowExplanation(false)}
											>
												×
											</button>
											<div className="hint-header">
												<h4>◆ Explanation</h4>
											</div>
											<MathJax>
												<div
													className="hint-text"
													dangerouslySetInnerHTML={{
														__html: currentQuestion.explanation,
													}}
												/>
											</MathJax>
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>
				{isModalOpen && (
					<HintModal
						onClose={() => setIsModalOpen(false)}
						onRequestHint={handleHintRequest}
					/>
				)}{' '}
				{/* Show modal when open */}
			</div>
		</MathJaxContext>
	);
};

const HintModal = ({ onClose, onRequestHint }) => (
	<div className="hint-modal-overlay">
		<div className="hint-modal-content">
			<button className="hint-close-button" onClick={onClose}>
				×
			</button>
			<div className="hint-option">
				<button className="partial-hint" onClick={() => onRequestHint('partial')}>
					Partial Hint
				</button>
				<p>Get a small clue without revealing the full answer.</p>
			</div>
			<div className="hint-option">
				<button className="step-hint" onClick={() => onRequestHint('step-hint')}>
					Step Hint
				</button>
				<p>Get a step hint to find the answer.</p>
			</div>
			<div className="hint-option">
				<button className="complete-hint" onClick={() => onRequestHint('complete')}>
					Complete Hint
				</button>
				<p>See the full explanation along with the correct answer.</p>
			</div>
		</div>
	</div>
);

export default QuizPage;
